import { Vector3 } from 'three';

/**
 * Keeps a fighter's body out of the floor, whatever its build and whatever the
 * clip.
 *
 * Every clip sets the hips' height from the rig it was captured on — X Bot, a
 * slim figure — scaled by hip height. That is right for where the hips go and
 * wrong for where the *skin* goes, because skin is as deep as the body is: on
 * fighter-b, 40 cm through the middle, a knockdown ends with him 20 cm under
 * the platform, and even lean fighter-a sinks 5-13 cm lying down and 7 cm
 * into a kick's standing foot. So each frame the body's lowest point is worked
 * out for the pose it is actually in, and the model is lifted until that
 * point is on the floor. It is a correction to the picture, not to the clip:
 * no clip is edited, and the gameplay position (`object3D.position`) is never
 * touched — the model inside it moves, and the bones, and the hitboxes posed
 * from them, move with it.
 *
 * Three rules keep it honest:
 *
 * **Lift, never pull down.** A body above the floor is left where the clip put
 * it, so a clip that is meant to leave the ground still does — the run's
 * flight phase, the jab's hop, anything aerial a future pack brings.
 *
 * **A dead band.** Standing clips already sit within a few centimetres of the
 * floor, a little in or a little out as the foot rolls, and none of them is
 * wrong enough to be worth touching. Nothing is corrected until the body is
 * more than `DEAD_BAND` under — the deepest any locomotion clip goes on either
 * fighter is 3.1 cm (fighter-b walking backwards), so walking, the guard and
 * backing off are exactly as they were.
 *
 * **The band is for feet.** It exists because a foot rolls on the floor; it
 * also hid a body *lying* 0-3.5 cm deep, and one just past it was barely
 * lifted — Nia Castellanos's knockdown ends 3.8 cm under and the ramp left her
 * 2.5 cm in. So the band applies only to samples a foot or a shin owns.
 * Anything else under the floor is lifted by its whole depth, which is
 * continuous from zero too, so the hand-over from foot to shoulder in a fall
 * has no step.
 *
 * **No step at the edge of the band, and no half measures past it.** The lift
 * is `depth - band·e^(-(depth - band)/(band/4))`: zero at the band, continuous
 * through it, and closing fast. The first ramp used the band itself as its
 * scale and left fighter-a's knockdown — 5.1 cm under — still 2.2 cm in the
 * floor; at a quarter of the band the same pose ends 0.6 cm in, and 20 cm
 * under ends on it, to a millionth of a millimetre.
 */
export const DEAD_BAND = 0.035;

/**
 * Directions a body part's extremes are taken in. 26 missed fighter-b's
 * lowest point by up to 1.6 cm — a gut is rounder than a cube's 26 faces,
 * edges and corners can follow — and 128 misses by 0.45 cm, at 0.12 ms a
 * fighter a frame.
 */
export const DIRECTION_COUNT = 128;

/** `n` directions spread evenly over a sphere (a Fibonacci lattice). */
function directions(n) {
  const list = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (2 * (i + 0.5)) / n;
    const r = Math.sqrt(1 - y * y);
    list.push(new Vector3(Math.cos(golden * i) * r, y, Math.sin(golden * i) * r));
  }
  return list;
}

/** How far `depth` metres under the floor should be lifted. 0 above the band. */
export function liftFor(depth, band = DEAD_BAND) {
  if (!(depth > band)) return 0;
  return depth - band * Math.exp(-(depth - band) / (band / 4));
}

/**
 * The few hundred vertices that can be the lowest point of a body.
 *
 * Skinning all twelve thousand every frame costs milliseconds; the lowest
 * point of a pose is always on the outside of some body part, so it is enough
 * to skin, for each bone, the vertices it owns that are furthest out in each
 * of `DIRECTION_COUNT` directions — about 800 on either fighter. Measured
 * against every vertex across every clip, the sampled lowest point is never
 * more than 0.6 cm above the true one (see `grounding.test.js`).
 */
export function contactSamples(mesh, count = DIRECTION_COUNT) {
  const towards = directions(count);
  const position = mesh.geometry.attributes.position;
  const skinIndex = mesh.geometry.attributes.skinIndex;
  const skinWeight = mesh.geometry.attributes.skinWeight;
  const bones = mesh.skeleton.bones.length;
  // Each vertex belongs to its single strongest bone.
  const owned = Array.from({ length: bones }, () => []);
  for (let v = 0; v < position.count; v++) {
    let best = 0;
    for (let k = 1; k < 4; k++) {
      if (skinWeight.getComponent(v, k) > skinWeight.getComponent(v, best)) best = k;
    }
    owned[skinIndex.getComponent(v, best)].push(v);
  }
  const picked = new Set();
  const p = new Vector3();
  for (const list of owned) {
    if (!list.length) continue;
    for (const d of towards) {
      let top = -Infinity;
      let at = -1;
      for (const v of list) {
        const reach = p.fromBufferAttribute(position, v).dot(d);
        if (reach > top) {
          top = reach;
          at = v;
        }
      }
      picked.add(at);
    }
  }
  return [...picked];
}

/**
 * For each sample, whether the bone that owns it most is a foot or a shin — what
 * rolls on the floor in a step. fighter-b's ankle, owned by the shin, goes
 * 0.9 cm under walking backwards; the thigh (`UpLeg`) never touches it standing.
 */
function footSamples(mesh, samples) {
  const skinIndex = mesh.geometry.attributes.skinIndex;
  const skinWeight = mesh.geometry.attributes.skinWeight;
  const bones = mesh.skeleton.bones;
  return samples.map((v) => {
    let best = 0;
    for (let k = 1; k < 4; k++) {
      if (skinWeight.getComponent(v, k) > skinWeight.getComponent(v, best)) best = k;
    }
    return /(Foot|[^p]Leg)$/.test(bones[skinIndex.getComponent(v, best)]?.name ?? '');
  });
}

/**
 * Grounding for one loaded fighter. `root` is the fighter's own object (whose
 * y is the floor), `model` the scene inside it that gets lifted, `mesh` the
 * skinned body. `update()` after the animator has posed the skeleton; it
 * returns the lift it applied, in metres.
 */
export function createGrounding({ root, model, mesh, samples = contactSamples(mesh) }) {
  const point = new Vector3();
  const onFoot = footSamples(mesh, samples);
  let lift = 0;
  let enabled = true;
  let lowFoot = Infinity;
  let lowRest = Infinity;

  /** The lowest sampled point of the pose, in `root`'s frame, before any lift. */
  function lowest() {
    model.position.y = 0;
    root.updateMatrixWorld(true);
    lowFoot = Infinity;
    lowRest = Infinity;
    for (let i = 0; i < samples.length; i++) {
      mesh.getVertexPosition(samples[i], point).applyMatrix4(mesh.matrixWorld);
      root.worldToLocal(point);
      if (onFoot[i]) lowFoot = Math.min(lowFoot, point.y);
      else lowRest = Math.min(lowRest, point.y);
    }
    return Math.min(lowFoot, lowRest);
  }

  return {
    samples,
    get lift() {
      return lift;
    },
    get enabled() {
      return enabled;
    },
    /** For a before-and-after picture; the game never turns it off. */
    setEnabled(next) {
      enabled = Boolean(next);
      if (!enabled) {
        lift = 0;
        model.position.y = 0;
      }
      return enabled;
    },
    lowest,
    update() {
      if (!enabled) return 0;
      lowest();
      // The band is for feet; anything else under the floor is lying on it.
      lift = Math.max(liftFor(-lowFoot), liftFor(-lowRest, 0));
      model.position.y = lift;
      return lift;
    },
  };
}
